import {
  useMutation as useMutationFromTanstackReactQuery,
  type DefaultError,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";

/**
 * Same as `@tanstack/react-query`'s `UseMutationOptions`, except `mutationKey`
 * is required. Every mutation in the app must declare a stable key so it can be
 * targeted by `useIsMutating`, scoped, and deduplicated.
 */
export type UseMutationOptionsWithRequiredKey<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TOnMutateResult = unknown,
> = UseMutationOptions<TData, TError, TVariables, TOnMutateResult> &
  Required<Pick<UseMutationOptions<TData, TError, TVariables, TOnMutateResult>, "mutationKey">>;

/**
 * Wrapper around `@tanstack/react-query`'s `useMutation` that makes `mutationKey`
 * mandatory. Prefer this over the upstream hook so every mutation is identifiable.
 */
export function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TOnMutateResult = unknown,
>(
  options: UseMutationOptionsWithRequiredKey<TData, TError, TVariables, TOnMutateResult>,
  queryClient?: QueryClient,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
  return useMutationFromTanstackReactQuery(options, queryClient);
}
